import { __awaiter } from 'tslib';
import { SegmentLoader } from './SegmentLoader.es.js';
import { getSanitizedPageName } from './pageName.es.js';
import { getPlatformName } from './platformName.es.js';
import { AnalyticsApplication } from './application.es.js';
import { ConvertExperimentLoader } from './ConvertExperimentLoader.es.js';

/**
 * This abstract service will be extended from the SegmentService that gets generated
 * by running the `setup-analytics` command.
 *
 * @export
 * @class AbstractSegmentService
 */
class AbstractSegmentServiceV2 {
    constructor(applicationName, windowObject, platformNameOverride) {
        this.disabled = false;
        this.applicationName = applicationName;
        this.segmentLoader = new SegmentLoader(windowObject);
        this.convertExperiment = new ConvertExperimentLoader(windowObject);
        this.window = windowObject;
        if (typeof this.window === 'undefined') {
            this.disabled = true;
        }
        else {
            if (platformNameOverride) {
                this.platformName = platformNameOverride;
            }
            else {
                this.platformName = getPlatformName(this.window);
            }
            this.subscribeToPageEvents();
        }
        this.setSegmentReadyPromise();
    }
    /**
     * Loads analytics snippet
     * @param segmentWriteKey
     */
    load(segmentWriteKey) {
        return this.segmentLoader.load(segmentWriteKey);
    }
    /**
     * Identifies the user's sessions (anonymous or logged in user)
     * @param userId
     * @param analyticsProperties a mix of auth state, reconciled data and platform provider data you can get using `getAnalyticsProperties`
     * @param userTraits
     */
    identify(userId, analyticsProperties, userTraits = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.disabled || !this.window.analytics) {
                return;
            }
            yield this.segmentReadyPromise;
            const analyticsUser = this.window.analytics.user && this.window.analytics.user();
            const analyticsGroup = this.window.analytics.group && this.window.analytics.group();
            if (analyticsUser === null || analyticsUser === void 0 ? void 0 : analyticsUser.traits) {
                analyticsUser === null || analyticsUser === void 0 ? void 0 : analyticsUser.traits({});
            }
            if (analyticsGroup === null || analyticsGroup === void 0 ? void 0 : analyticsGroup.traits) {
                analyticsGroup === null || analyticsGroup === void 0 ? void 0 : analyticsGroup.traits({});
            }
            if (userId) {
                this.window.analytics.identify(userId, userTraits);
                // Links anonymous identify for Chameleon to the identified user
                if (analyticsUser && analyticsUser.anonymousId) {
                    this.window.analytics.alias(userId, analyticsUser.anonymousId(), {
                        integrations: {
                            Chameleon: true,
                        },
                    });
                }
                this.sendSignedUpEventIfNecessary(analyticsProperties);
                this.sendLoggedInEventIfNecessary(analyticsProperties);
            }
            else {
                // We only reset the user id in segment SDK. Anonymous identifies are not necessary. Amplitude doesn't need it, and we're not
                // willing to pay for Chameleon MAUs that includes anonymous users
                analyticsUser === null || analyticsUser === void 0 ? void 0 : analyticsUser.id(null);
            }
        });
    }
    reset() {
        if (this.disabled || !this.window.analytics) {
            return;
        }
        this.window.analytics.reset();
    }
    /**
     *
     * @param pageLanguage
     * @param pageNameOverride should only be provided for apps with virtual routes like AngularJS
     * @param applicationNameOverride should ONLY be used by the legacy-mfe because it handles multiple application.
     */
    page(pageLanguage, pageNameOverride, applicationNameOverride) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.disabled || !this.window.analytics) {
                return;
            }
            yield this.segmentReadyPromise;
            const pageName = pageNameOverride ? pageNameOverride : getSanitizedPageName(this.window.location.pathname);
            this.window.analytics.page(pageName, {
                application_name: applicationNameOverride || this.applicationName,
                language: pageLanguage,
                platform: this.platformName,
                /**
                 * Manually set path and url to ensure they are always the same as the location's pathname and href.
                 * This is necessary because Segment defaults to the canonical url to set "path" and "url" in the page event properties.
                 *
                 * More context: https://github.com/LandrAudio/LANDR.Web.Core/pull/247
                 */
                path: this.window.location.pathname,
                url: this.window.location.href,
            });
        });
    }
    // eslint-disable-next-line @typescript-eslint/ban-types
    track(eventName, properties, applicationNameOverride) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.disabled || !this.window.analytics) {
                return Promise.resolve();
            }
            yield this.segmentReadyPromise;
            // In case ad blockers don't load segment, this will ensure it's not blocking code waiting for the promise
            const timeoutPromise = new Promise((resolve) => {
                setTimeout(resolve, 2000);
            });
            const trackPromise = new Promise((resolve) => {
                this.window.analytics.track(eventName, Object.assign(Object.assign({}, this.getDefaultTrackProperties(applicationNameOverride)), properties), {}, resolve);
            });
            return Promise.race([trackPromise, timeoutPromise]);
        });
    }
    getDefaultTrackProperties(applicationNameOverride) {
        var _a, _b, _c, _d, _e, _f, _g;
        return {
            application_name: applicationNameOverride || this.applicationName,
            platform: this.platformName,
            page_language: (_a = this.window.landrAnalytics) === null || _a === void 0 ? void 0 : _a.currentPageProperties.language,
            page_name: (_b = this.window.landrAnalytics) === null || _b === void 0 ? void 0 : _b.currentPageProperties.name,
            page_path: (_c = this.window.landrAnalytics) === null || _c === void 0 ? void 0 : _c.currentPageProperties.path,
            page_referrer: (_d = this.window.landrAnalytics) === null || _d === void 0 ? void 0 : _d.currentPageProperties.referrer,
            page_search: (_e = this.window.landrAnalytics) === null || _e === void 0 ? void 0 : _e.currentPageProperties.search,
            page_title: (_f = this.window.landrAnalytics) === null || _f === void 0 ? void 0 : _f.currentPageProperties.title,
            page_url: (_g = this.window.landrAnalytics) === null || _g === void 0 ? void 0 : _g.currentPageProperties.url,
        };
    }
    subscribeToPageEvents() {
        var _a;
        if (this.disabled || !this.window.analytics) {
            return;
        }
        // Only one instance will subscribe to page events, the rest will use `this.window.currentPageProperties`
        if (!((_a = this.window.landrAnalytics) === null || _a === void 0 ? void 0 : _a.subscribedToPageEvents)) {
            // Set landrAnalytics data in window so other instances can access it
            this.window.landrAnalytics = {
                currentPageProperties: {
                    language: 'en',
                    name: '',
                    path: '',
                    referrer: '',
                    search: '',
                    title: '',
                    url: '',
                },
                subscribedToPageEvents: true,
            };
            this.window.analytics.on('page', (_, __, pageProperties) => {
                this.window.landrAnalytics.currentPageProperties = pageProperties;
            });
        }
    }
    setSegmentReadyPromise() {
        const timeoutPromise = new Promise((resolve) => {
            setTimeout(resolve, 3000);
        });
        const segmentReadyPromise = new Promise((resolve) => {
            var _a;
            if ((_a = this.window) === null || _a === void 0 ? void 0 : _a.analytics) {
                this.window.analytics.ready(() => {
                    resolve();
                });
            }
        });
        this.segmentReadyPromise = Promise.race([segmentReadyPromise, timeoutPromise]);
    }
    sendSignedUpEventIfNecessary(analyticsProperties) {
        if (!analyticsProperties) {
            return;
        }
        // Profile survey doesn't include `https://api.landr.com/` in keys
        const keyPrefix = 'is_signup' in analyticsProperties ? '' : 'https://api.landr.com/';
        if (analyticsProperties[`${keyPrefix}is_signup`]) {
            this.window.analytics.track('Signed Up - Front End', {
                application_name: AnalyticsApplication.Account,
                signup_referrer_application: analyticsProperties[`${keyPrefix}signup_referrer_application`],
                signup_method: analyticsProperties[`${keyPrefix}signup_method`],
                landr_email: analyticsProperties[`${keyPrefix}signup_landr_email`],
                first_name: analyticsProperties[`${keyPrefix}signup_first_name`],
                last_name: analyticsProperties[`${keyPrefix}signup_last_name`],
                signup_referrer: analyticsProperties[`${keyPrefix}signup_referrer`],
                utm_term: analyticsProperties[`${keyPrefix}signup_utm_term`],
                utm_source: analyticsProperties[`${keyPrefix}signup_utm_source`],
                utm_medium: analyticsProperties[`${keyPrefix}signup_utm_medium`],
                utm_content: analyticsProperties[`${keyPrefix}signup_utm_content`],
                utm_campaign: analyticsProperties[`${keyPrefix}signup_utm_campaign`],
            });
        }
    }
    sendLoggedInEventIfNecessary(analyticsProperties) {
        if (!analyticsProperties) {
            return;
        }
        const keyPrefix = 'https://api.landr.com/';
        if (analyticsProperties[`${keyPrefix}is_login`]) {
            this.window.analytics.track('Logged In - Front End', {
                application_name: AnalyticsApplication.Account,
                login_method: analyticsProperties[`${keyPrefix}login_login_method`],
                is_impersonate: analyticsProperties[`${keyPrefix}login_is_impersonate`],
                utm_term: analyticsProperties[`${keyPrefix}login_utm_term`],
                utm_source: analyticsProperties[`${keyPrefix}login_utm_source`],
                utm_medium: analyticsProperties[`${keyPrefix}login_utm_medium`],
                utm_content: analyticsProperties[`${keyPrefix}login_utm_content`],
                utm_campaign: analyticsProperties[`${keyPrefix}login_utm_campaign`],
            });
        }
    }
}

export { AbstractSegmentServiceV2 };
